import { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PageLoader } from "@/components/PageLoader";

const blank = {
  employee_id: "",
  name: "",
  whatsapp: "",
  role: "pegawai",
  password: "",
  pay_type: "monthly",
  salary: 0,
  hourly_rate: 0,
  overtime_rate: 0,
  max_cash_advance_pct: 20,
};

export default function AdminEmployees() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        const { password, ...rest } = form;
        const body = password ? { ...rest, password } : rest;
        await api.put(`/users/${editId}`, body);
        toast.success("Pegawai diupdate");
      } else {
        await api.post("/users", form);
        toast.success("Pegawai dibuat");
      }
      setOpen(false);
      setForm(blank);
      setEditId(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const edit = (u) => {
    setEditId(u.id);
    setForm({ ...blank, ...u, password: "" });
    setOpen(true);
  };

  const del = async (u) => {
    if (!window.confirm(`Hapus ${u.name}? Semua data terkait akan ikut terhapus.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Pegawai dihapus");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto">
      {loading && <PageLoader label="Memuat data pegawai..." />}
      {!loading && (<>
      <div className="flex items-center justify-between">
        <div>
          <p className="overline">Master Data</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Pegawai</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(blank); } }}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-employee" className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-1.5" /> Tambah
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Edit Pegawai" : "Tambah Pegawai"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="form-employee">
              <Field label="Employee ID" required>
                <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} required disabled={!!editId} data-testid="emp-eid" />
              </Field>
              <Field label="Nama" required>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="emp-name" />
              </Field>
              <Field label="WhatsApp">
                <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="emp-wa" />
              </Field>
              <Field label={editId ? "Password Baru (kosongkan jika tidak ubah)" : "Password"} required={!editId}>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editId} data-testid="emp-pw" />
              </Field>
              <Field label="Tipe Gaji">
                <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                  <SelectTrigger data-testid="emp-paytype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Bulanan / Tetap</SelectItem>
                    <SelectItem value="hourly">PHL / Per Jam</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Gaji Bulanan (Rp)">
                  <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: parseFloat(e.target.value) || 0 })} data-testid="emp-salary" />
                </Field>
                <Field label="Hourly Rate (Rp/jam)">
                  <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} data-testid="emp-hourly" />
                </Field>
                <Field label="Overtime Rate (Rp/jam)">
                  <Input type="number" value={form.overtime_rate} onChange={(e) => setForm({ ...form, overtime_rate: parseFloat(e.target.value) || 0 })} data-testid="emp-ot" />
                </Field>
                <Field label="Max Kasbon %">
                  <Input type="number" value={form.max_cash_advance_pct} onChange={(e) => setForm({ ...form, max_cash_advance_pct: parseFloat(e.target.value) || 0 })} data-testid="emp-pct" />
                </Field>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90" data-testid="btn-save-emp">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mt-6 border border-zinc-200 shadow-none overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee ID</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Tipe</TableHead>
              <TableHead className="text-right">Gaji</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`row-user-${u.employee_id}`}>
                <TableCell className="font-mono text-xs">{u.employee_id}</TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell>{u.pay_type === "hourly" ? "PHL" : "Bulanan"}</TableCell>
                <TableCell className="text-right">{formatRupiah(u.salary)}</TableCell>
                <TableCell className="text-right">{u.pay_type === "hourly" ? `${formatRupiah(u.hourly_rate)}/jam` : `${formatRupiah(u.overtime_rate)}/jam`}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => edit(u)} data-testid={`btn-edit-${u.employee_id}`}><Edit className="w-4 h-4" /></Button>
                  {u.role !== "admin" && (
                    <Button variant="ghost" size="icon" onClick={() => del(u)} data-testid={`btn-del-${u.employee_id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      </>)}
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <Label className="text-xs">{label}{required && " *"}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
